/* eslint-disable react/no-danger */
import { LinkAnchorButton } from "lib/miolo";
import { getYear } from "date-fns";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import { default as Head } from "next/head";
import Link from "next/link";
import numeral from "numeral";
import { useAmplitude } from "../../lib/amplitude/Amplitude";
import getBaseUrl from "../../lib/utils/getBaseUrl";
import * as palette from "../palette";

interface IGatedAmericanCompanyProps {
  company: IAmericanCompanyIssuerTopLevelFragment;
  tickerSymbol: string;
}

export default function GatedAmericanCompanyCard({ company, tickerSymbol }: IGatedAmericanCompanyProps) {
  const amplitude = useAmplitude();

  return (
    <main
      css={{
        display: "grid",
        gridRowGap: 32,
        padding: "48px 0",
      }}
    >
      <Head>
        <title>{`${tickerSymbol} | Fundamentei`}</title>

        <meta name="title" content={`${company.name} | Fundamentei`} />
        <meta name="description" content={company.businessDescription} />

        <meta name="og:type" property="og:type" content="website" />
        <meta name="og:site_name" property="og:site_name" content="Fundamentei" />
        <meta name="og:title" property="og:title" content={`${company.name} | Fundamentei`} />
        <meta name="og:description" property="og:description" content={company.businessDescription} />
        <meta
          name="og:image"
          property="og:image"
          content={`${getBaseUrl()}/static/branding/wordmark/White on Blue@4x.jpg`}
        />
        <meta name="og:image:type" property="og:image:type" content="image/jpg" />
        <meta name="og:image:width" property="og:image:width" content="3344" />
        <meta name="og:image:height" property="og:image:height" content="976" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@fundamentei" />
        <meta name="twitter:title" content={`${company.name} | Fundamentei`} />
        <meta name="twitter:image" content={`${getBaseUrl()}/static/branding/wordmark/White on Blue@4x.jpg`} />
        <meta name="twitter:image:alt" content="Logotipo do Fundamentei" />

        <link rel="canonical" href={`${getBaseUrl()}/us/${tickerSymbol}`} key="canonical" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "http://schema.org",
              "@type": "Corporation",
              name: company.name,
              tickerSymbol,
              url: company.websiteHref,
              description: company.businessDescription,
            }),
          }}
        />
      </Head>

      <div
        css={{
          display: "grid",
          gridColumnGap: 30,
          gridTemplateColumns: "repeat(12, 1fr)",
          alignItems: "center",
          width: 1140,
          margin: "0px auto",
        }}
      >
        <div
          css={{
            gridColumn: "4 / span 6",
            backgroundColor: "#ffffff",
            borderRadius: 8,
            boxShadow: "0 15px 35px 0 #33333333",
          }}
        >
          <div
            css={{
              padding: 28,
            }}
          >
            <div
              css={{
                display: "grid",
                gridColumnGap: 16,
                gridTemplateColumns: "1fr max-content",
                justifyContent: "space-between",
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridRowGap: 8,
                }}
              >
                <div
                  css={{
                    minWidth: 0,
                  }}
                >
                  <h1
                    title={company.name}
                    css={{
                      margin: 0,
                      overflow: "hidden",
                      color: palette.fiord,
                      fontWeight: "bold",
                      fontSize: 33,
                      lineHeight: 1.33,
                      letterSpacing: 0.7,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {company.name}
                  </h1>

                  {company.irs && (
                    <span
                      css={{
                        color: palette.lynch,
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      {`IRS: ${company.irs}`}
                    </span>
                  )}
                </div>

                <div
                  css={{
                    display: "grid",
                    gridAutoColumns: "min-content",
                    gridAutoFlow: "column",
                    gridColumnGap: 6,
                  }}
                >
                  <span
                    css={{
                      color: palette.blueBayoux,
                      fontWeight: "bold",
                      fontSize: 14,
                      lineHeight: 1.33,
                      letterSpacing: 0.7,
                    }}
                  >
                    {tickerSymbol}
                  </span>
                </div>
              </div>

              {company.standardLogo && (
                <img
                  role="presentation"
                  src={company.standardLogo.url}
                  onContextMenu={(event) => event.preventDefault()}
                  onMouseDown={(event) => event.preventDefault()}
                  width={158}
                  height={48}
                  alt={company.name}
                  draggable={false}
                  css={{
                    objectFit: "contain",
                    objectPosition: "right",
                    userSelect: "none",
                    height: 48,
                  }}
                />
              )}
            </div>

            <div
              css={{
                marginTop: 20,
              }}
            >
              <p
                css={{
                  margin: 0,
                  padding: 0,
                  color: palette.baliHai,
                  fontWeight: 300,
                  fontSize: 14,
                  lineHeight: 1.33,
                  letterSpacing: 1,
                }}
              >
                {company.businessDescription}
              </p>
            </div>

            <div
              css={{
                marginTop: 20,
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridAutoFlow: "column",
                  gridTemplateColumns: "max-content 1fr",
                  gridColumnGap: 24,
                }}
              >
                {company.foundedYear && (
                  <div>
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.tundora,
                      }}
                    >
                      Founded
                    </div>

                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.baliHai,
                        marginTop: 4,
                      }}
                    >
                      {company.foundedYear}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div
              css={{
                marginTop: 20,
                color: palette.lynch,
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridAutoColumns: "max-content",
                  gridAutoFlow: "column",
                  gridColumnGap: 12,
                  color: palette.lynch,
                }}
              >
                {company.investorRelationsHref && (
                  <>
                    <a
                      title="Investor Relations"
                      target="_blank"
                      href={company.investorRelationsHref}
                      rel="noopener noreferrer"
                      onClick={() => {
                        amplitude.logEvent("Click on a link in details box", {
                          tickerSymbol,
                          title: "Investor Relations",
                          href: company.investorRelationsHref,
                        });
                      }}
                      css={{
                        color: palette.link,
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Investor Relations
                    </a>

                    <span>•</span>
                  </>
                )}

                <a
                  title="SEC"
                  target="_blank"
                  href={company.edgarHref}
                  rel="noopener noreferrer"
                  onClick={() => {
                    amplitude.logEvent("Click on a link in details box", {
                      tickerSymbol,
                      title: "SEC",
                      href: company.websiteHref,
                    });
                  }}
                  css={{
                    color: palette.link,
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: 1.33,
                    letterSpacing: 0.5,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  SEC
                </a>
              </div>
            </div>

            {company.modernFinancials.yearly.length > 0 && (
              <div
                css={{
                  marginTop: 20,
                }}
              >
                <div
                  css={{
                    display: "grid",
                    gridAutoColumns: "max-content",
                    gridAutoFlow: "column",
                    gridColumnGap: 24,
                  }}
                >
                  <div>
                    <div
                      css={{
                        color: palette.tundora,
                        fontWeight: "bold",
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      Revenue
                    </div>

                    <div
                      css={{
                        marginTop: 4,
                        color: palette.viridian,
                        fontWeight: "bold",
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      <span>
                        {formatNetRevenue(company.modernFinancials.yearly[0].incomeStatement.revenue)} (
                        {getYear(parseDateYmd(company.modernFinancials.yearly[0].reportDate))})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              css={{
                marginTop: 20,
              }}
            >
              <hr
                css={{
                  border: `solid 1px ${palette.catskillWhite}`,
                }}
              />
            </div>

            <div
              css={{
                marginTop: 20,
                textAlign: "center",
              }}
            >
              <Link href="/sign-up" legacyBehavior={true}>
                <LinkAnchorButton
                  href="/"
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    letterSpacing: 1,
                  }}
                >
                  Want to see more? Sign up!
                </LinkAnchorButton>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function formatNetRevenue(netRevenue: number): string {
  const formattedNetRevenue = numeral(netRevenue).format("0 a");
  return formattedNetRevenue.replace("b", "billion").replace("m", "million");
}
